import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Download,
  FileText,
  Package,
  RefreshCw,
  ShieldAlert,
  Timer,
} from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatNumber, formatDate } from "@/utils/format";
import {
  fetchOrcamentoAnaliseSolicitacao,
  type OrcamentoAnaliseSolicitacaoData,
  type OrcamentoAnaliseVeredito,
} from "@/services/orcamento";
import { cn } from "@/utils/cn";

function vereditoStyles(v: OrcamentoAnaliseVeredito): { wrap: string; badge: string; label: string } {
  if (v === "critico") {
    return {
      wrap: "bg-gradient-to-br from-red-50 via-rose-50/90 to-red-50/70 border-red-200/90",
      badge: "bg-red-600 text-white",
      label: "Risco de estouro",
    };
  }
  if (v === "atencao") {
    return {
      wrap: "bg-gradient-to-br from-amber-50 to-orange-50/60 border-amber-200",
      badge: "bg-amber-500 text-white",
      label: "Atenção",
    };
  }
  return {
    wrap: "bg-gradient-to-br from-emerald-50 to-teal-50/50 border-emerald-200",
    badge: "bg-emerald-600 text-white",
    label: "Dentro do orçamento",
  };
}

function fmtQty(n: number, unit: string): string {
  const u = (unit ?? "").trim();
  return `${formatNumber(n, 3)}${u ? ` ${u}` : ""}`;
}

function realizPeriodDisplay(result: OrcamentoAnaliseSolicitacaoData | null): { start: string; end: string } {
  if (result?.period?.startDate && result?.period?.endDate) {
    return { start: result.period.startDate, end: result.period.endDate };
  }
  return {
    start: "2024-01-01",
    end: new Date().toISOString().slice(0, 10),
  };
}

function buildAnaliseCsv(data: OrcamentoAnaliseSolicitacaoData): string {
  const sep = ";";
  const rows: string[] = [];
  rows.push(`Análise SC × Orçamento`);
  rows.push(`SC${sep}${data.purchaseRequestId}`);
  rows.push(`Obra (buildingId SC)${sep}${data.buildingId}`);
  rows.push(`Centro de custo${sep}${data.costCenter.name || ""}`);
  rows.push(`Período realizado${sep}${data.period.startDate}${sep}${data.period.endDate}`);
  rows.push(`Total SC (qtd.)${sep}${String(data.totalSolicitacao).replace(".", ",")}`);
  rows.push(`Estouros (linhas)${sep}${data.resumo.linhasComExcesso}`);
  rows.push(`Sem orçamento${sep}${data.resumo.linhasSemOrcamento}`);
  rows.push("");
  rows.push(
    `Item${sep}Descrição${sep}Recurso${sep}UC${sep}SC (qtd.)${sep}Orçado (qtd.)${sep}Realizado (qtd.)${sep}Após SC (qtd.)${sep}% após SC${sep}Status`
  );
  for (const it of data.items) {
    for (const row of it.analysisRows) {
      const pct = row.pctApos != null ? formatNumber(row.pctApos, 1) : "";
      const st = row.excede ? "Estouro" : row.semOrcamentoNestaUc ? "UC sem linha" : row.orcado > 0.0001 ? "OK" : "";
      rows.push(
        [
          it.itemNumber,
          it.description.replace(/;/g, " "),
          it.resourceKey,
          row.ucName.replace(/;/g, " "),
          row.allocatedValue,
          row.orcado,
          row.realizado,
          row.simulado,
          pct,
          st,
        ].join(sep)
      );
    }
  }
  return rows.join("\r\n");
}

function downloadAnaliseCsv(data: OrcamentoAnaliseSolicitacaoData): void {
  const csv = buildAnaliseCsv(data);
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `analise-sc-${data.purchaseRequestId}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function SolicitacaoOrcamentoModal({
  open,
  onClose,
  filterBuildingId,
  startDate,
  endDate,
}: {
  open: boolean;
  onClose: () => void;
  filterBuildingId: string;
  startDate: string;
  endDate: string;
}) {
  const [scId, setScId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OrcamentoAnaliseSolicitacaoData | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (exportWrapRef.current && !exportWrapRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [exportMenuOpen]);

  const run = useCallback(() => {
    const id = scId.trim();
    if (!id) {
      setError("Informe o número da solicitação de compras.");
      return;
    }
    if (!filterBuildingId) {
      setError("Selecione uma obra nos filtros da página antes de analisar.");
      return;
    }
    setLoading(true);
    setError(null);
    fetchOrcamentoAnaliseSolicitacao({
      purchaseRequestId: id,
      startDate,
      endDate,
      filterBuildingId,
    })
      .then(setResult)
      .catch((e: unknown) => {
        setResult(null);
        setError(e instanceof Error ? e.message : "Não foi possível concluir a análise.");
      })
      .finally(() => setLoading(false));
  }, [scId, startDate, endDate, filterBuildingId]);

  const vs = result ? vereditoStyles(result.resumo.veredito) : null;
  const sameUnitAcrossItems =
    result &&
    result.items.length > 0 &&
    result.items.every((i) => (i.unit || "").trim() === (result.items[0]!.unit || "").trim());
  const commonUnit = sameUnitAcrossItems ? (result?.items[0]?.unit ?? "").trim() : "";
  const { start: pStart, end: pEnd } = realizPeriodDisplay(result);
  const mensagemPrincipal =
    result && result.resumo.mensagens.length > 0 ? result.resumo.mensagens[0] : "";

  const titleEl = (
    <div className="flex items-start gap-3 min-w-0 pr-2">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 flex-shrink-0 shadow-sm">
        <ClipboardCheck className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0 pt-0.5">
        <h2 className="text-lg font-bold text-slate-900 leading-tight tracking-tight">
          Análise de solicitação x Orçamento
        </h2>
        <p className="text-xs text-slate-500 mt-1 leading-snug">
          Quantidades orçadas e realizadas da obra da SC no Sienge; UCs por item conforme apropriações (%)
        </p>
      </div>
    </div>
  );

  const footerEl = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 w-full">
      <div className="relative flex-shrink-0" ref={exportWrapRef}>
        <Button
          type="button"
          variant="secondary"
          size="md"
          disabled={!result}
          rightIcon={<ChevronDown className={cn("w-4 h-4 transition-transform", exportMenuOpen && "rotate-180")} />}
          onClick={() => result && setExportMenuOpen((o) => !o)}
        >
          Exportar relatório
        </Button>
        {exportMenuOpen && result && (
          <div className="absolute right-0 bottom-full mb-1 z-10 min-w-[200px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => {
                downloadAnaliseCsv(result);
                setExportMenuOpen(false);
              }}
            >
              <Download className="w-4 h-4 text-slate-500" />
              Planilha CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} size="full" title={titleEl} footer={footerEl}>
      <div className="w-full min-w-0 max-w-none space-y-4 pb-1">
        {/* Linha SC + período */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
              Nº da solicitação (SC)
            </label>
            <div className="flex gap-2 items-stretch">
              <div className="relative flex-1 min-w-0">
                <FileText
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  type="text"
                  value={scId}
                  onChange={(e) => setScId(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && run()}
                  placeholder="Ex.: 3369"
                  className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50/80 pl-10 pr-3 text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
                />
              </div>
              <Button
                type="button"
                variant="primary"
                size="md"
                className="shrink-0 px-5"
                onClick={run}
                loading={loading}
                disabled={!filterBuildingId}
                leftIcon={loading ? undefined : <RefreshCw className="w-4 h-4" />}
              >
                Carregar e comparar
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
              Período considerado
            </span>
            <div className="flex items-center gap-2.5 text-slate-900">
              <CalendarDays className="w-5 h-5 text-slate-400 flex-shrink-0" aria-hidden />
              <span className="text-sm font-semibold tabular-nums tracking-tight">
                {formatDate(pStart)} <span className="text-slate-400 font-normal">→</span> {formatDate(pEnd)}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 font-medium">Realizado</p>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-slate-500">
            <Loader size="lg" label="Carregando dados da solicitação…" />
          </div>
        )}

        {result && vs && !loading && (
          <>
            <div className={cn("rounded-xl border p-4 sm:p-5 shadow-sm min-w-0 w-full", vs.wrap)}>
              <div className="flex flex-col gap-4 min-w-0 w-full">
                <div className="flex gap-3 min-w-0 w-full">
                  <div
                    className={cn(
                      "flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border bg-white/90",
                      result.resumo.veredito === "critico" && "border-red-200 text-red-600",
                      result.resumo.veredito === "atencao" && "border-amber-200 text-amber-600",
                      result.resumo.veredito === "ok" && "border-emerald-200 text-emerald-600"
                    )}
                  >
                    <ShieldAlert className="w-6 h-6" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex flex-wrap items-center gap-2 gap-y-1">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                          vs.badge
                        )}
                      >
                        {vs.label}
                      </span>
                      <span className="text-sm font-bold text-slate-900">
                        SC {result.purchaseRequestId}
                        {result.buildingName && (
                          <span className="font-semibold text-slate-500"> · {result.buildingName}</span>
                        )}
                      </span>
                      {result.resumo.veredito === "ok" && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-hidden />
                      )}
                      {(result.resumo.veredito === "atencao" || result.resumo.veredito === "critico") && (
                        <AlertTriangle
                          className={cn(
                            "w-4 h-4",
                            result.resumo.veredito === "critico" ? "text-red-600" : "text-amber-600"
                          )}
                          aria-hidden
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 w-full min-w-0 shrink-0">
                  <div className="relative overflow-hidden rounded-xl border border-white/80 bg-white p-3 shadow-sm min-w-0">
                    <Package className="absolute right-2 top-2 h-4 w-4 text-slate-300" aria-hidden />
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide pr-6">
                      Total SC (quantidade)
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-900 tabular-nums leading-tight">
                      {commonUnit
                        ? fmtQty(result.totalSolicitacao, commonUnit)
                        : `${formatNumber(result.totalSolicitacao, 3)}${result.items.length > 1 ? " *" : ""}`}
                    </p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl border border-white/80 bg-white p-3 shadow-sm min-w-0">
                    <Building2 className="absolute right-2 top-2 h-4 w-4 text-slate-300" aria-hidden />
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide pr-6">
                      Centro de custo
                    </p>
                    <p className="mt-1 text-xs font-bold text-slate-800 line-clamp-3 leading-snug">
                      {result.costCenter.name?.trim() || "—"}
                    </p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl border border-white/80 bg-white p-3 shadow-sm min-w-0">
                    <ShieldAlert className="absolute right-2 top-2 h-4 w-4 text-slate-300" aria-hidden />
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide pr-6">
                      Estouros (linhas)
                    </p>
                    <p className="mt-1 text-lg font-bold tabular-nums text-red-600">
                      {formatNumber(result.resumo.linhasComExcesso)}
                    </p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl border border-white/80 bg-white p-3 shadow-sm min-w-0">
                    <Timer className="absolute right-2 top-2 h-4 w-4 text-slate-300" aria-hidden />
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide pr-6">
                      Sem orçamento
                    </p>
                    <p className="mt-1 text-lg font-bold tabular-nums text-amber-800">
                      {formatNumber(result.resumo.linhasSemOrcamento)}
                    </p>
                  </div>
                </div>

                {(mensagemPrincipal || result.resumo.mensagens.length > 1) && (
                  <div className="block w-full min-w-0 max-w-full shrink-0 rounded-lg border border-black/5 bg-white/70 px-3 py-3 sm:px-4 sm:py-3.5">
                    {mensagemPrincipal && (
                      <p className="text-sm text-slate-800 leading-relaxed whitespace-normal break-words [overflow-wrap:anywhere]">
                        {mensagemPrincipal}
                      </p>
                    )}
                    {result.resumo.mensagens.length > 1 && (
                      <ul
                        className={cn(
                          "space-y-1.5 text-xs text-slate-600",
                          mensagemPrincipal && "mt-3 pt-3 border-t border-slate-200/80",
                        )}
                      >
                        {result.resumo.mensagens.slice(1).map((m, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-brand-500 font-bold shrink-0">·</span>
                            <span className="min-w-0 flex-1 whitespace-normal break-words [overflow-wrap:anywhere]">
                              {m}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <BarChart3 className="w-4 h-4 text-slate-400" />
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                Itens e simulação por UC (quantidades)
              </span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <div className="space-y-3">
              {result.items.map((it) => (
                <div
                  key={it.itemNumber}
                  className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 bg-slate-50/90 px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wide text-red-600">Item {it.itemNumber}</p>
                      <p className="text-sm font-semibold text-slate-900 mt-0.5">{it.description}</p>
                      {it.resourceKey && (
                        <p className="text-[11px] text-slate-500 mt-1 font-mono">Recurso: {it.resourceKey}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Qtd. linha</p>
                      <p className="text-sm font-bold text-slate-900 tabular-nums">{fmtQty(it.quantity, it.unit)}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-100 bg-white">
                          <th className="text-left px-4 py-2.5 font-bold text-slate-500 uppercase tracking-wide">
                            UC
                          </th>
                          <th className="text-right px-3 py-2.5 font-bold text-slate-500 uppercase tracking-wide">
                            SC (qtd.)
                          </th>
                          <th className="text-right px-3 py-2.5 font-bold text-slate-500 uppercase tracking-wide">
                            Orçado (qtd.)
                          </th>
                          <th className="text-right px-3 py-2.5 font-bold text-slate-500 uppercase tracking-wide">
                            Realizado (qtd.)
                          </th>
                          <th className="text-right px-3 py-2.5 font-bold text-slate-500 uppercase tracking-wide">
                            Após SC (qtd.)
                          </th>
                          <th className="text-right px-3 py-2.5 font-bold text-slate-500 uppercase tracking-wide">
                            % após SC
                          </th>
                          <th className="text-center px-3 py-2.5 font-bold text-slate-500 uppercase tracking-wide">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {it.analysisRows.map((row, ri) => (
                          <tr key={ri} className="hover:bg-slate-50/80">
                            <td className="px-4 py-2.5 text-slate-800">
                              <span className="flex items-center gap-2 max-w-[220px]">
                                <Building2 className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" aria-hidden />
                                <span className="leading-snug">{row.ucName}</span>
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
                              {fmtQty(row.allocatedValue, it.unit)}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                              {row.orcado > 0.0001 ? fmtQty(row.orcado, it.unit) : "—"}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                              {fmtQty(row.realizado, it.unit)}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900">
                              {fmtQty(row.simulado, it.unit)}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums">
                              {row.pctApos != null ? (
                                <span
                                  className={cn(
                                    "font-bold",
                                    row.excede && "text-red-600",
                                    !row.excede && row.pctApos > 80 && "text-amber-600",
                                    !row.excede && row.pctApos <= 80 && "text-emerald-700"
                                  )}
                                >
                                  {formatNumber(row.pctApos, 1)}%
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              {row.excede && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-800">
                                  <AlertTriangle className="w-3 h-3" aria-hidden />
                                  Estouro
                                </span>
                              )}
                              {!row.excede && row.semOrcamentoNestaUc && (
                                <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900">
                                  UC sem linha
                                </span>
                              )}
                              {!row.excede && row.orcado > 0.0001 && !row.semOrcamentoNestaUc && (
                                <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                                  OK
                                </span>
                              )}
                              {!row.excede && row.orcado <= 0.0001 && !row.semOrcamentoNestaUc && (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!result && !loading && !error && (
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-10 text-slate-500">
            <p className="text-sm text-center px-4 max-w-md">
              Informe o <strong>nº da solicitação</strong> e clique em <strong>Carregar e comparar</strong> para buscar
              os dados no Sienge e exibir a análise por quantidade e UC.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
